import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Anchor, Copy, Loader2, Hash, MessageSquare, Sparkles, Bookmark } from 'lucide-react';
import { copyToClipboard } from '../utils/clipboard';

const PERSIST_KEY = 'creatorflow_writing';
const PERSIST_MINUTES = 5;

const WritingTools = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('hooks');
  const [loading, setLoading] = useState(false);
  
  // Load persisted data on mount
  const loadPersisted = (key, defaultVal) => {
    try {
      const saved = localStorage.getItem(`${PERSIST_KEY}_${key}`);
      if (saved) {
        const { data, timestamp } = JSON.parse(saved);
        if (Date.now() - timestamp < PERSIST_MINUTES * 60 * 1000) return data;
        localStorage.removeItem(`${PERSIST_KEY}_${key}`);
      }
    } catch (e) {}
    return defaultVal;
  };

  // Hooks state
  const [hookTopic, setHookTopic] = useState('');
  const [selectedFrameworks, setSelectedFrameworks] = useState(['shock']);
  const [hooks, setHooks] = useState(() => loadPersisted('hooks', []));

  // Captions state
  const [captionTopic, setCaptionTopic] = useState('');
  const [captionStyle, setCaptionStyle] = useState('pov');
  const [captions, setCaptions] = useState(() => loadPersisted('captions', []));

  // Hashtags state
  const [hashtagTopic, setHashtagTopic] = useState('');
  const [hashtagPlatform, setHashtagPlatform] = useState('tiktok');
  const [hashtags, setHashtags] = useState(() => loadPersisted('hashtags', []));

  // Persist hooks when they change
  useEffect(() => {
    if (hooks.length > 0) {
      localStorage.setItem(`${PERSIST_KEY}_hooks`, JSON.stringify({ data: hooks, timestamp: Date.now() }));
    }
  }, [hooks]);

  // Persist captions when they change
  useEffect(() => {
    if (captions.length > 0) {
      localStorage.setItem(`${PERSIST_KEY}_captions`, JSON.stringify({ data: captions, timestamp: Date.now() }));
    }
  }, [captions]);

  // Persist hashtags when they change
  useEffect(() => {
    if (hashtags.length > 0) {
      localStorage.setItem(`${PERSIST_KEY}_hashtags`, JSON.stringify({ data: hashtags, timestamp: Date.now() }));
    }
  }, [hashtags]);

  const hookFrameworks = [
    { id: 'shock', label: 'Shock/Surprise' },
    { id: 'curiosity', label: 'Curiosity Gap' },
    { id: 'benefit', label: 'Direct Benefit' },
    { id: 'story', label: 'Story Starter' },
    { id: 'statistic', label: 'Statistic/Fact' }
  ];

  const toggleFramework = (framework) => {
    setSelectedFrameworks(prev => 
      prev.includes(framework) 
        ? prev.filter(f => f !== framework)
        : [...prev, framework]
    );
  };

  const generateHooks = async (e) => {
    e.preventDefault();
    if (!hookTopic) {
      toast.error('Please enter a topic');
      return;
    }
    if (selectedFrameworks.length === 0) {
      toast.error('Please select at least one framework');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/writing/hooks', { topic: hookTopic, framework: selectedFrameworks[0] });
      setHooks(res.data.hooks);
      toast.success('Hooks generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate hooks');
    } finally {
      setLoading(false);
    }
  };

  const generateCaptions = async (e) => {
    e.preventDefault();
    if (!captionTopic) {
      toast.error('Please enter a topic');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/writing/captions', { topic: captionTopic, style: captionStyle });
      setCaptions(res.data.captions);
      toast.success('Captions generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate captions');
    } finally {
      setLoading(false);
    }
  };

  const generateHashtags = async (e) => {
    e.preventDefault();
    if (!hashtagTopic) {
      toast.error('Please enter a topic');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/writing/hashtags', { topic: hashtagTopic, platform: hashtagPlatform });
      setHashtags(res.data.hashtags);
      toast.success('Hashtags generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate hashtags');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text) => {
    copyToClipboard(text);
    toast.success('Copied to clipboard!');
  };

  const saveHook = async (hook) => {
    try {
      await api.post('/saved', {
        type: 'hook',
        title: hook,
        content: { hook: hook, topic: hookTopic }
      });
      toast.success('Hook saved to library!');
    } catch (err) {
      toast.error('Failed to save hook');
    }
  };

  const copyAllHashtags = () => {
    copyToClipboard(hashtags.join(' '));
    toast.success('All hashtags copied!');
  };

  const tabs = [
    { id: 'hooks', label: 'Hooks', icon: Anchor },
    { id: 'captions', label: 'Captions', icon: MessageSquare },
    { id: 'hashtags', label: 'Hashtags', icon: Hash }
  ];

  return (
    <div className="space-y-6" data-testid="writing-tools">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold tracking-tight mb-2 bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
          Writing Tools
        </h1>
        <p className="text-slate-400 text-lg">
          Create compelling copy for your short-form content.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-full transition-all ${
              activeTab === tab.id 
                ? 'bg-violet-500 text-white' 
                : 'bg-transparent text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Form */}
        <Card className="bg-slate-800/30 border-slate-700/50 h-fit">
          <CardContent className="p-6">
            {/* HOOKS TAB */}
            {activeTab === 'hooks' && (
              <form onSubmit={generateHooks} className="space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <Anchor className="w-5 h-5 text-violet-400" />
                  <h3 className="text-lg font-bold text-white">Hooks</h3>
                </div>
                <p className="text-slate-400 text-sm mb-6">
                  Create scroll-stopping hooks for short-form videos.
                </p>

                <div className="space-y-2">
                  <Label className="text-white font-medium">Video Topic</Label>
                  <Input
                    data-testid="hook-topic"
                    placeholder="e.g. How to save money on groceries"
                    value={hookTopic}
                    onChange={(e) => setHookTopic(e.target.value)}
                    className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>

                <div className="space-y-3">
                  <Label className="text-white font-medium">Hook Frameworks</Label>
                  {hookFrameworks.map((framework) => (
                    <div key={framework.id} className="flex items-center space-x-3">
                      <Checkbox
                        id={framework.id}
                        checked={selectedFrameworks.includes(framework.id)}
                        onCheckedChange={() => toggleFramework(framework.id)}
                        className="border-violet-500 data-[state=checked]:bg-violet-500 data-[state=checked]:border-violet-500"
                      />
                      <label
                        htmlFor={framework.id}
                        className="text-slate-300 text-sm cursor-pointer"
                      >
                        {framework.label}
                      </label>
                    </div>
                  ))}
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-14 bg-gradient-to-r from-violet-500 to-cyan-500 hover:opacity-90 text-white text-lg font-medium"
                  data-testid="generate-hooks-btn"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Anchor className="w-5 h-5 mr-2" />}
                  Generate Hooks
                </Button>
                <p className="text-center text-slate-500 text-sm">Uses 2 credits</p>
              </form>
            )}

            {/* CAPTIONS TAB */}
            {activeTab === 'captions' && (
              <form onSubmit={generateCaptions} className="space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-5 h-5 text-cyan-400" />
                  <h3 className="text-lg font-bold text-white">Captions</h3>
                </div>
                <p className="text-slate-400 text-sm mb-6">
                  Write engaging captions that drive action.
                </p>

                <div className="space-y-2">
                  <Label className="text-white font-medium">Video Topic</Label>
                  <Input
                    data-testid="caption-topic"
                    placeholder="e.g. Morning routine tips"
                    value={captionTopic}
                    onChange={(e) => setCaptionTopic(e.target.value)}
                    className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white font-medium">Caption Style</Label>
                  <Select value={captionStyle} onValueChange={setCaptionStyle}>
                    <SelectTrigger data-testid="caption-style" className="bg-slate-900/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="pov">POV Style</SelectItem>
                      <SelectItem value="curiosity">Curiosity Style</SelectItem>
                      <SelectItem value="cta">CTA Style</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-14 bg-gradient-to-r from-violet-500 to-cyan-500 hover:opacity-90 text-white text-lg font-medium"
                  data-testid="generate-captions-btn"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <MessageSquare className="w-5 h-5 mr-2" />}
                  Generate Captions
                </Button>
                <p className="text-center text-slate-500 text-sm">Uses 2 credits</p>
              </form>
            )}

            {/* HASHTAGS TAB */}
            {activeTab === 'hashtags' && (
              <form onSubmit={generateHashtags} className="space-y-6">
                <div className="flex items-center gap-2 mb-2">
                  <Hash className="w-5 h-5 text-pink-400" />
                  <h3 className="text-lg font-bold text-white">Hashtags</h3>
                </div>
                <p className="text-slate-400 text-sm mb-6">
                  Get the best hashtags for maximum reach.
                </p>

                <div className="space-y-2">
                  <Label className="text-white font-medium">Content Topic</Label>
                  <Input
                    data-testid="hashtag-topic"
                    placeholder="e.g. Fitness motivation"
                    value={hashtagTopic}
                    onChange={(e) => setHashtagTopic(e.target.value)}
                    className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white font-medium">Platform</Label>
                  <Select value={hashtagPlatform} onValueChange={setHashtagPlatform}>
                    <SelectTrigger data-testid="hashtag-platform" className="bg-slate-900/50 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="tiktok">TikTok</SelectItem>
                      <SelectItem value="reels">Instagram Reels</SelectItem>
                      <SelectItem value="shorts">YouTube Shorts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-14 bg-gradient-to-r from-violet-500 to-cyan-500 hover:opacity-90 text-white text-lg font-medium"
                  data-testid="generate-hashtags-btn"
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Hash className="w-5 h-5 mr-2" />}
                  Generate Hashtags
                </Button>
                <p className="text-center text-slate-500 text-sm">Uses 2 credits</p>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Right Column - Results */}
        <div className="space-y-4">
          {/* Empty States */}
          {activeTab === 'hooks' && hooks.length === 0 && (
            <div className="border-2 border-dashed border-slate-700/50 rounded-xl p-12 text-center">
              <div className="flex justify-center mb-4">
                <Anchor className="w-16 h-16 text-slate-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-400 mb-2">Ready to go viral?</h3>
              <p className="text-slate-500">
                Enter a topic and select frameworks to generate powerful hooks.
              </p>
            </div>
          )}

          {activeTab === 'captions' && captions.length === 0 && (
            <div className="border-2 border-dashed border-slate-700/50 rounded-xl p-12 text-center">
              <div className="flex justify-center mb-4">
                <MessageSquare className="w-16 h-16 text-slate-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-400 mb-2">Captions that convert</h3>
              <p className="text-slate-500">
                Enter your topic to generate engaging captions.
              </p>
            </div>
          )}

          {activeTab === 'hashtags' && hashtags.length === 0 && (
            <div className="border-2 border-dashed border-slate-700/50 rounded-xl p-12 text-center">
              <div className="flex justify-center mb-4">
                <Hash className="w-16 h-16 text-slate-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-400 mb-2">Boost your reach</h3>
              <p className="text-slate-500">
                Enter a topic to get trending hashtags.
              </p>
            </div>
          )}

          {/* Hooks Results */}
          {activeTab === 'hooks' && hooks.length > 0 && (
            <div className="space-y-3">
              {hooks.map((hook, idx) => (
                <Card key={idx} className="bg-slate-800/30 border-slate-700/50" data-testid="hook-result">
                  <CardContent className="p-4 flex items-center justify-between gap-4">
                    <p className="text-white flex-1">{hook}</p>
                    <div className="flex gap-1 shrink-0">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => saveHook(hook)} 
                        className="text-slate-400 hover:text-violet-400"
                        data-testid="save-hook-btn"
                      >
                        <Bookmark className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleCopy(hook)} 
                        className="text-slate-400 hover:text-white"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Captions Results */}
          {activeTab === 'captions' && captions.length > 0 && (
            <div className="space-y-3">
              {captions.map((caption, idx) => (
                <Card key={idx} className="bg-slate-800/30 border-slate-700/50" data-testid="caption-result">
                  <CardContent className="p-4 flex items-center justify-between gap-4">
                    <p className="text-white flex-1">{caption}</p>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleCopy(caption)} 
                      className="text-slate-400 hover:text-white shrink-0"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Hashtags Results */}
          {activeTab === 'hashtags' && hashtags.length > 0 && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={copyAllHashtags} 
                  className="border-slate-700 text-slate-300" 
                  data-testid="copy-all-hashtags"
                >
                  <Copy className="w-4 h-4 mr-2" /> Copy All
                </Button>
              </div>
              <Card className="bg-slate-800/30 border-slate-700/50">
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-2">
                    {hashtags.map((tag, idx) => (
                      <span 
                        key={idx} 
                        className="px-3 py-1.5 bg-slate-900/50 text-cyan-400 rounded-lg text-sm cursor-pointer hover:bg-slate-800 transition-colors"
                        onClick={() => handleCopy(tag)}
                        data-testid="hashtag-badge"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WritingTools;
